import { useContext, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../Context';
import { AuthContextApi } from '../../Context/api';
import {
    CalendarIcon,
    InformationCircleIcon,
    BuildingOfficeIcon
} from '@heroicons/react/24/outline'
import { toast } from 'react-toastify';
import CardMetricas from '../../Components/Cards/Metricas';
import Header from '../../Components/Header';
import FormBusca from '../../Components/Forms/Busca';
import { colorMap } from '../../Common';
import ClientesTable from '../../Components/Table/Clientes';
import ModalCliente from '../../Components/Modal/Clientes';
import ModalVisualizacaoCliente from './ModalVisualizacao';
import { jsonConfig } from '../../Config';

const Clientes = () => {

    const { states, setters } = useContext(AuthContext)
    const {
        checkLogin,
        getClientes,
        postCliente,
        patchCliente,
        deleteCliente,
        getCep
    } = useContext(AuthContextApi)


    const [query, setQuery] = useState('')
    const tableScrollRef = useRef(null)
    const tableContainerRef = useRef(null)

    const [showLeftArrow, setShowLeftArrow] = useState(false)
    const [showRightArrow, setShowRightArrow] = useState(true)
    const [arrowTop, setArrowTop] = useState('50%')

    const [searchTerm, setSearchTerm] = useState('')
    const [selectedFilterField, setSelectedFilterField] = useState('nome_empresarial')
    const [selectedSortField, setSelectedSortField] = useState('data_criacao')
    const [sortDirection, setSortDirection] = useState('desc')
    const [recordsPerPage, setRecordsPerPage] = useState(10)

    const [modalVisualizacao, setModalVisualizacao] = useState(false)
    const [clienteSelecionado, setClienteSelecionado] = useState(null)

    const filterOptions = [
        { value: 'nome_empresarial', label: 'Nome Empresarial' },
        { value: 'nome_fantasia', label: 'Nome Fantasia' },
        { value: 'cnpj', label: 'CNPJ' },
        { value: 'municipio', label: 'Município' },
        { value: 'uf', label: 'UF' }
    ]

    const sortOptions = [
        { value: 'nome_empresarial', label: 'Nome Empresarial' },
        { value: 'nome_fantasia', label: 'Nome Fantasia' },
        { value: 'cnpj', label: 'CNPJ' },
        { value: 'data_abertura', label: 'Data de Abertura' },
        { value: 'situacao_cadastral', label: 'Situação Cadastral' },
        { value: 'data_criacao', label: 'Data de Criação' }
    ]

    const primeiroLoad = async () => {
        const params = new URLSearchParams({
            page: 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: ''
        })
        await checkLogin()
        await getClientes(params.toString())
    }

    const loadData = async () => {
        const params = new URLSearchParams({
            page: states.page || 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: searchTerm
        })
        await getClientes(params.toString())
    }

    const actionModalClienteEdit = async (id, data) => {
        const response = await patchCliente(id, data)
        if (response) {
            setters.setModalCliente(false)
            primeiroLoad()
        }
    }

    const actionModalClienteCreate = async (data) => {
        const response = await postCliente(data)
        if (response) {
            setters.setModalCliente(false)
            primeiroLoad()
        }
    }

    const deleteClienteAction = async (cliente) => {
        setters.setModalMsg(true)
        setters.setMsgModalMsg('Excluir Cliente')
        setters.setDescModalMsg(`Tem certeza que deseja excluir o cliente "${cliente.nome_empresarial}"? Esta ação não pode ser desfeita.`)
        setters.setPerigoModalMsg(true)
        setters.setTitleButtonModalMsg('Excluir')
        setters.setFunctionModalMsg(() => async () => {
            setters.setModalMsg(false)
            const resp = await deleteCliente(cliente.id)
            if (resp) {
                primeiroLoad()
            }
            setters.setModalMsg(false)
        })
    }

    const next = async () => {
        setters.setPage(states.page + 1)
    }

    const previous = async () => {
        if (states.page > 0) {
            setters.setPage(states.page - 1)
        }
    }

    const search = async (data) => {
        const params = new URLSearchParams({
            page: states.page || 0,
            per_page: recordsPerPage,
            sort: selectedSortField,
            sort_dir: sortDirection,
            filter: selectedFilterField,
            search: searchTerm
        })
        await getClientes(params.toString())
    }

    const clear = async () => {
        setters.setPage(0)
        const params = new URLSearchParams({
            page: 0,
            per_page: 10,
            sort: 'data_criacao',
            sort_dir: 'desc',
            filter: 'nome_empresarial',
            search: ''
        })
        await getClientes(params.toString())
    }

    const color = 'green'
    const colorClasses = colorMap[color] || colorMap.green

    const userPermission = localStorage.getItem('permisssion')
    const isAdmin = userPermission === jsonConfig.roles.admin

    const addCliente = () => {
        setters.setIsEdit(false)
        setters.setCliente({})
        setters.setModalCliente(true)
    }

    const editCliente = (cliente) => {
        setters.setIsEdit(true)
        setters.setCliente(cliente)
        setters.setModalCliente(true)
    }

    const viewCliente = (cliente) => {
        setClienteSelecionado(cliente)
        setModalVisualizacao(true)
    }

    const checkScrollPosition = () => {
        if (tableScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current
            const isAtStart = scrollLeft <= 1
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1
            setShowLeftArrow(!isAtStart)
            setShowRightArrow(!isAtEnd)
        }
    }

    const calculateArrowPosition = () => {
        if (tableContainerRef.current && tableScrollRef.current) {
            const containerRect = tableContainerRef.current.getBoundingClientRect()
            const scrollRect = tableScrollRef.current.getBoundingClientRect()
            const headerHeight = scrollRect.top - containerRect.top
            const tableHeight = scrollRect.height
            const topPosition = headerHeight + (tableHeight / 2)
            const containerHeight = containerRect.height
            const percentage = (topPosition / containerHeight) * 100
            setArrowTop(`${percentage}%`)
        }
    }

    const headers = [
        {
            label: 'Ações',
            icon: BuildingOfficeIcon,
            key: 'actions'
        },
        {
            label: 'CNPJ',
            icon: InformationCircleIcon,
            key: 'cnpj'
        },
        {
            label: 'Nome',
            icon: InformationCircleIcon,
            key: 'nome_fantasia'
        },
        {
            label: 'Município',
            icon: InformationCircleIcon,
            key: 'municipio'
        },
        {
            label: 'UF',
            icon: InformationCircleIcon,
            key: 'uf'
        },
        {
            label: 'Criação',
            icon: CalendarIcon,
            key: 'data_criacao'
        }
    ]

    useEffect(() => {
        const tableElement = tableScrollRef.current
        if (tableElement) {
            const timer = setTimeout(() => {
                checkScrollPosition()
                calculateArrowPosition()
            }, 1)

            tableElement.addEventListener('scroll', checkScrollPosition)
            window.addEventListener('resize', () => {
                checkScrollPosition()
                calculateArrowPosition()
            })

            return () => {
                clearTimeout(timer)
                tableElement.removeEventListener('scroll', checkScrollPosition)
                window.removeEventListener('resize', checkScrollPosition)
            }
        }
    }, [states.dataClientes, states.loading])

    useEffect(() => {
        primeiroLoad()

        return () => {
            setQuery('')
            setters.setDataClientes([])
            setters.setCliente({})
            setters.setModalCliente(false)
        }
    }, [])

    useEffect(() => {
        if (states.dataClientes.length > 0) {
            search()
        }
    }, [states.page])

    return (
        <>
            <Header
                title="Clientes"
                description="Gerencie os clientes do sistema"
                icon={BuildingOfficeIcon}
                buttonText="Adicionar Cliente"
                buttonAction={addCliente}
                hasAction={true}
                hasReturn={false}
                color='green'
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {isAdmin && <CardMetricas dataMetricas={null} color={color} />}
                <FormBusca
                    filterOptions={filterOptions}
                    sortOptions={sortOptions}
                    search={search}
                    handleClear={clear}
                    color={color}
                    items={{
                        searchTerm,
                        setSearchTerm,
                        selectedFilterField,
                        setSelectedFilterField,
                        selectedSortField,
                        setSelectedSortField,
                        sortDirection,
                        setSortDirection,
                        recordsPerPage,
                        setRecordsPerPage,
                    }}
                    haveData={states.dataClientes.length > 0}
                />

                <ClientesTable
                    data={states.dataClientes}
                    loading={states.loading}
                    title="Clientes"
                    headers={headers}
                    viewCliente={viewCliente}
                    editCliente={editCliente}
                    deleteCliente={deleteClienteAction}
                    color={color}
                    previous={previous}
                    next={next}
                    page={states.page}
                    isAdmin={isAdmin}
                />
            </div>

            <ModalCliente
                states={states}
                setters={setters}
                actionCreate={actionModalClienteCreate}
                actionEdit={actionModalClienteEdit}
                color={color}
                getCep={getCep}
            />

            <ModalVisualizacaoCliente
                isOpen={modalVisualizacao}
                onClose={() => setModalVisualizacao(false)}
                cliente={clienteSelecionado}
                color={color}
            />
        </>
    )
}

export default Clientes;

